import { useEffect } from 'react';
import {
  FieldError,
  FieldName,
  FieldValues,
  UseFormMethods,
} from 'react-hook-form';
import { set } from 'lodash';
import isEmail from 'validator/lib/isEmail';
import validator from 'validator';
import { phoneRegExp } from '@upstart/harmony/lib/validation/user';
// eslint-disable-next-line import/named
import { FlattenKeys } from '../types/utilTypes';

export const emailValidatorFunction = (data: string) =>
  !data || !isEmail(data) ? 'Valid email address required' : true;

export const optionalEmailValidator = (data: string) =>
  !data || isEmail(data) ? true : 'Valid email address required';

export const phoneValidator = {
  value: phoneRegExp,
  message: 'Valid phone number required "(###) ###-####"',
};

export const zipValidator = {
  value: /^\d{5}(?:[-\s]\d{4})?$/,
  message: 'Valid zip required',
};

export const colorValidator = {
  value: /^#([A-Fa-f0-9]{6})$/,
  message: 'Valid hex color required',
};

export const wholeNumberValidator = {
  value: /^\d+$/,
  message: 'Valid number required',
};

export const decimalValidator = {
  value: /^\d+(.\d+)?$/,
  message: 'Valid number required',
};

export const optionalPercentValidator = {
  value: /^\d+(.\d+)?%?$/, // allows an optional percent suffix
  message: 'Valid percent required',
};

export const currencyValidator = {
  value: /^\d+(\.\d\d?)?$/,
  message: 'Valid amount required',
};

export const wholeNumberValidatorFunction = (data: string | undefined) => {
  if (!data || !wholeNumberValidator.value.test(data))
    return wholeNumberValidator.message;
  return '';
};

export const bankRoutingNumberValidation = (data: string | undefined) => {
  if (!data || data.length !== 9 || !wholeNumberValidator.value.test(data))
    return `Enter 9-digit routing number`;
  return true;
};

export const bankAccountNumberValidation = (data: string | undefined) => {
  if (
    !data ||
    data.length < 6 ||
    data.length > 17 ||
    !wholeNumberValidator.value.test(data)
  )
    return `Enter a number from 6 to 17 digits`;
  return true;
};

export const decimalValidatorFunction = (data: string | undefined) => {
  if (!data || !decimalValidator.value.test(data))
    return decimalValidator.message;
  return '';
};

export const currencyValidatorFunction = (
  data: string | number | undefined,
) => {
  if (!data || !currencyValidator.value.test(data.toString())) {
    return 'Valid amount required';
  }
  return '';
};

export const getValidationErrorMessage = (validationError?: FieldError) => {
  if (!validationError) return undefined;
  else if (validationError.message) return validationError.message;
  else if (validationError.type === 'required') return 'This field is required';
  return 'Invalid input';
};

export const triggerValidation = <T extends FieldValues>(
  form: UseFormMethods<T>,
  name: FlattenKeys<T>,
) => {
  form.trigger((name as unknown) as FieldName<T>);
  // FIXME: this does not work with react-hook-form >=6.12.2. To upgrade further, we would likely
  // likely need to go to v7 and and use `setValue` with `shouldValidate: true` set
  set(form.formState.touched, name, true);
};

export const useTriggerValidationOnMount = <T extends FieldValues>(
  form: UseFormMethods<T> | undefined,
  name: FlattenKeys<T>,
) => {
  useEffect(() => {
    requestAnimationFrame(() => {
      if (form?.getValues(name)) {
        triggerValidation(form, name);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const urlValidator = (data: string) =>
  !data || !validator.isURL(data)
    ? 'Valid Privacy Policy URL is required'
    : true;
